import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { coercePerfSettings } from '../lib/perfSettings';
import { migrateCurrentSelectionFilter } from '../lib/scopeFilter';
import {
  knownOrgOf,
  linkVenuesFromUrl,
  mergeChangedPerfSettings,
  migrateLegacyViewContext,
  readLegacyViewContext,
  readSavedViewContext,
  rememberOrgOf,
  sameViewContextState,
  savedVenuesFor,
  saveViewContextParts,
  stateFromSaved,
  urlRegionFilter,
  viewContextFromUrl,
  type ContextPart,
  type LinkVenues,
  type ViewContextState,
} from '../lib/viewContext';
import type {
  PerfSettings,
  ReservationSourceId,
  ReservationStatusGroup,
} from '../types';

/**
 * Where the context on screen came from:
 * - `saved`: the person's own, for this org (or one they changed);
 * - `link`: a shared link (or a reload of one), kept for this visit; its
 *   parts become the person's only when they change them or keep it;
 * - `default`: nothing saved for this org yet.
 */
export type ViewContextOrigin = 'saved' | 'link' | 'default';

export type SetContextOptions = {
  /**
   * An automatic correction (a shift the venues don't have, a group that is
   * gone), not a choice: applied, but saved only when the context on screen
   * is the person's own, so a link never overwrites their saved context.
   */
  adjust?: boolean;
};

export type ViewContextStore = {
  state: ViewContextState;
  origin: ViewContextOrigin;
  /** The org the context is kept under, once known. */
  orgId: string | null;
  /**
   * The venues the shared link of this page load named (decision 7.7.2), or
   * null: OpsCommand tells what it showed and dropped (`venues.link`).
   */
  link: LinkVenues | null;
  setRegionFilter: (value: string | null, opts?: SetContextOptions) => void;
  setPerfSettings: (value: PerfSettings, opts?: SetContextOptions) => void;
  setStatus: (value: ReservationStatusGroup[], opts?: SetContextOptions) => void;
  setSource: (value: ReservationSourceId[], opts?: SetContextOptions) => void;
  setShifts: (value: string[], opts?: SetContextOptions) => void;
  /** Make a link's whole context the person's saved one for this org. */
  keepLinkContext: () => void;
  /**
   * Leave a link's context for the person's own saved one for this org (or
   * the defaults): "Back to my view" of the context bar's link notice.
   */
  restoreSavedContext: () => void;
  /** True when this part is the person's own (not only a link's), so it may be saved. */
  ownsPart: (part: ContextPart) => boolean;
};

type Args = {
  /** The person (user id, or the auth-disabled sentinel); null keeps nothing. */
  ownerId: string | null;
  restaurantId: string;
  /** The restaurant's org from the server; falls back to the last one seen. */
  orgId: string | null;
  /** The venues App restored for this restaurant (`insights:lastScope`). */
  initialRegionFilter?: string | null;
  /** Defaults to the page's URL; tests pass one. */
  search?: string;
};

type Boot = {
  state: ViewContextState;
  origin: ViewContextOrigin;
  orgId: string | null;
  link: LinkVenues | null;
};

/** The person's saved context for this org and restaurant, or the defaults when none is saved. */
function ownContextOf(
  ownerId: string | null,
  orgId: string | null,
  restaurantId: string | undefined,
  initialRegionFilter: string | null | undefined,
): { state: ViewContextState; origin: ViewContextOrigin } | null {
  if (!ownerId || !orgId) return null;
  const saved = readSavedViewContext(ownerId, orgId);
  const chosen = restaurantId ? savedVenuesFor(saved, restaurantId) : null;
  return {
    state: { regionFilter: migrateCurrentSelectionFilter(chosen ?? initialRegionFilter ?? 'all'), ...stateFromSaved(saved) },
    origin: saved ? 'saved' : 'default',
  };
}

/**
 * The context a page load starts with: a link's when the URL names one
 * (absent parts are defaults), else the person's saved context for this
 * org, else the defaults. Before the org is known, the former global values
 * stand in, read only, so an upgrade does not flash the defaults first.
 */
export function bootViewContext(args: {
  ownerId: string | null;
  orgId: string | null;
  search: string;
  initialRegionFilter?: string | null;
  /** The restaurant opened: its saved venue choice for the org applies (decision 7.7.1). */
  restaurantId?: string;
}): Boot {
  const link = viewContextFromUrl(args.search);
  if (link) {
    // The person's own view reloaded (every URL the app writes names the
    // context) is theirs, not a shared link's.
    const own = ownContextOf(args.ownerId, args.orgId, args.restaurantId, args.initialRegionFilter);
    const asWritten = { ...link, regionFilter: migrateCurrentSelectionFilter(urlRegionFilter(args.search)) };
    if (own && sameViewContextState(asWritten, own.state)) {
      return { state: asWritten, origin: own.origin, orgId: args.orgId, link: null };
    }
    return {
      state: { ...link, regionFilter: migrateCurrentSelectionFilter(link.regionFilter) },
      origin: 'link',
      orgId: args.orgId,
      link: linkVenuesFromUrl(args.search),
    };
  }
  // A scope saved before sets of venues had their own filter reads as those venues.
  const initial = args.initialRegionFilter ?? 'all';
  if (args.ownerId && args.orgId) {
    const saved = readSavedViewContext(args.ownerId, args.orgId) ?? migrateLegacyViewContext(args.ownerId, args.orgId);
    // The last venues chosen at this restaurant for this customer, else the
    // ones App restored, else the restaurant alone.
    const chosen = args.restaurantId ? savedVenuesFor(saved, args.restaurantId) : null;
    return {
      state: { regionFilter: migrateCurrentSelectionFilter(chosen ?? initial), ...stateFromSaved(saved) },
      origin: saved ? 'saved' : 'default',
      orgId: args.orgId,
      link: null,
    };
  }
  const legacy = args.ownerId ? readLegacyViewContext() : null;
  return {
    state: { regionFilter: migrateCurrentSelectionFilter(initial), ...stateFromSaved(legacy) },
    origin: 'default',
    orgId: null,
    link: null,
  };
}

const PART_KEYS: Record<'perfSettings' | 'status' | 'source' | 'shifts' | 'venues', ContextPart[]> = {
  perfSettings: ['period', 'comparison'],
  status: ['statuses'],
  source: ['sources'],
  shifts: ['shifts'],
  venues: ['venues'],
};

/**
 * The single source of truth for the view context on the client (Phase B
 * step 1). Owns the venues, period, comparison and filters every screen
 * reads; persists them per person and customer (org); restores a shared
 * link without overwriting the person's saved context for that org.
 * OpsCommand keeps it in sync with the URL (useOpsUrlSync) and hands it to
 * every screen and request (ViewContextProvider, lib/requestContext.ts).
 */
export function useViewContextStore({
  ownerId,
  restaurantId,
  orgId: orgIdProp,
  initialRegionFilter,
  search,
}: Args): ViewContextStore {
  const orgId = orgIdProp ?? knownOrgOf(restaurantId);
  const [boot] = useState<Boot>(() =>
    bootViewContext({
      ownerId,
      orgId,
      search: search ?? (typeof window === 'undefined' ? '' : window.location.search),
      initialRegionFilter,
      restaurantId,
    }),
  );
  const [state, setState] = useState<ViewContextState>(boot.state);
  const [origin, setOrigin] = useState<ViewContextOrigin>(boot.origin);
  const stateRef = useRef(state);
  stateRef.current = state;
  const originRef = useRef(origin);
  originRef.current = origin;
  const orgRef = useRef<string | null>(boot.orgId);
  const [resolvedOrg, setResolvedOrg] = useState<string | null>(boot.orgId);
  const ownerRef = useRef(ownerId);
  ownerRef.current = ownerId;
  const restaurantRef = useRef(restaurantId);
  restaurantRef.current = restaurantId;
  const initialRef = useRef(initialRegionFilter);
  const searchRef = useRef(search ?? (typeof window === 'undefined' ? '' : window.location.search));
  /** Parts the person changed on this page (a link's parts they made theirs). */
  const touched = useRef<Set<ContextPart>>(new Set());

  const markTouched = (parts: ContextPart[]) => {
    for (const part of parts) touched.current.add(part);
    if (originRef.current === 'default') {
      originRef.current = 'saved';
      setOrigin('saved');
    }
  };

  const save = (key: keyof typeof PART_KEYS, value: unknown, opts?: SetContextOptions) => {
    const owner = ownerRef.current;
    const org = orgRef.current;
    if (!owner || !org) return;
    if (opts?.adjust && originRef.current === 'link') return;
    saveViewContextParts(owner, org, { [key]: value });
  };

  // The org became known, or the person opened a restaurant of another org.
  useEffect(() => {
    if (!orgId) return;
    rememberOrgOf(restaurantId, orgId);
    const previous = orgRef.current;
    if (previous === orgId) return;
    orgRef.current = orgId;
    setResolvedOrg(orgId);
    const owner = ownerRef.current;
    if (previous === null) {
      // First answer for this page load. A link keeps its context, unless it
      // is the person's own view reloaded.
      if (originRef.current === 'link' && owner && !touched.current.size) {
        const own = ownContextOf(owner, orgId, restaurantRef.current, initialRef.current);
        const asWritten = { ...stateRef.current, regionFilter: migrateCurrentSelectionFilter(urlRegionFilter(searchRef.current)) };
        if (own && sameViewContextState(asWritten, own.state)) {
          setState(asWritten);
          originRef.current = own.origin;
          setOrigin(own.origin);
        }
        return;
      }
      if (originRef.current === 'link' || !owner) return;
      const saved = readSavedViewContext(owner, orgId) ?? migrateLegacyViewContext(owner, orgId);
      if (!touched.current.size) {
        if (saved) {
          const chosen = savedVenuesFor(saved, restaurantRef.current);
          setState(prev => ({
            ...prev,
            ...stateFromSaved(saved),
            ...(chosen ? { regionFilter: migrateCurrentSelectionFilter(chosen) } : {}),
          }));
          setOrigin('saved');
        }
        return;
      }
      // Changed before the org was known: those choices are theirs here.
      const now = stateRef.current;
      saveViewContextParts(owner, orgId, {
        perfSettings: now.perfSettings,
        status: now.status,
        source: now.source,
        shifts: now.shifts,
        ...(touched.current.has('venues')
          ? { venues: { restaurantId: restaurantRef.current, regionFilter: now.regionFilter } }
          : {}),
      });
      return;
    }
    // Another customer: its own saved context, never the previous org's.
    touched.current.clear();
    const saved = owner ? readSavedViewContext(owner, orgId) : null;
    setState(prev => ({ ...prev, ...stateFromSaved(saved) }));
    setOrigin(saved ? 'saved' : 'default');
  }, [orgId, restaurantId]);

  const setRegionFilter = useCallback((value: string | null, opts?: SetContextOptions) => {
    setState(prev => (prev.regionFilter === value ? prev : { ...prev, regionFilter: value }));
    // Remembered per person and customer, with the restaurant it was chosen at.
    save('venues', { restaurantId: restaurantRef.current, regionFilter: value }, opts);
    if (!opts?.adjust) markTouched(['venues']);
  }, []);

  const setPerfSettings = useCallback((value: PerfSettings, opts?: SetContextOptions) => {
    const next = coercePerfSettings(value);
    const previous = stateRef.current.perfSettings;
    setState(prev => ({ ...prev, perfSettings: next }));
    if (originRef.current === 'link' && !opts?.adjust) {
      // Only the half the person changed becomes theirs.
      const owner = ownerRef.current;
      const org = orgRef.current;
      if (owner && org) {
        const saved = readSavedViewContext(owner, org);
        saveViewContextParts(owner, org, {
          perfSettings: mergeChangedPerfSettings(saved?.perfSettings, previous, next),
        });
      }
    } else {
      save('perfSettings', next, opts);
    }
    if (!opts?.adjust) markTouched(PART_KEYS.perfSettings);
  }, []);

  const setStatus = useCallback((value: ReservationStatusGroup[], opts?: SetContextOptions) => {
    setState(prev => ({ ...prev, status: value }));
    save('status', value, opts);
    if (!opts?.adjust) markTouched(PART_KEYS.status);
  }, []);

  const setSource = useCallback((value: ReservationSourceId[], opts?: SetContextOptions) => {
    setState(prev => ({ ...prev, source: value }));
    save('source', value, opts);
    if (!opts?.adjust) markTouched(PART_KEYS.source);
  }, []);

  const setShifts = useCallback((value: string[], opts?: SetContextOptions) => {
    setState(prev => ({ ...prev, shifts: value }));
    save('shifts', value, opts);
    if (!opts?.adjust) markTouched(PART_KEYS.shifts);
  }, []);

  const keepLinkContext = useCallback(() => {
    const owner = ownerRef.current;
    const org = orgRef.current;
    const now = stateRef.current;
    if (owner && org) {
      saveViewContextParts(owner, org, {
        perfSettings: now.perfSettings,
        status: now.status,
        source: now.source,
        shifts: now.shifts,
        venues: { restaurantId: restaurantRef.current, regionFilter: now.regionFilter },
      });
    }
    touched.current.add('venues');
    originRef.current = 'saved';
    setOrigin('saved');
  }, []);

  const restoreSavedContext = useCallback(() => {
    const owner = ownerRef.current;
    const org = orgRef.current;
    const saved = owner && org ? readSavedViewContext(owner, org) : null;
    const chosen = saved ? savedVenuesFor(saved, restaurantRef.current) : null;
    touched.current.clear();
    setState({ regionFilter: migrateCurrentSelectionFilter(chosen ?? 'all'), ...stateFromSaved(saved) });
    originRef.current = saved ? 'saved' : 'default';
    setOrigin(originRef.current);
  }, []);

  // Re-created when the origin changes, so effects that read it re-run.
  const ownsPart = useMemo(
    () => (part: ContextPart) => origin !== 'link' || touched.current.has(part),
    [origin],
  );

  return useMemo(
    () => ({
      state,
      origin,
      orgId: resolvedOrg,
      link: boot.link,
      setRegionFilter,
      setPerfSettings,
      setStatus,
      setSource,
      setShifts,
      keepLinkContext,
      restoreSavedContext,
      ownsPart,
    }),
    [state, origin, resolvedOrg, boot.link, setRegionFilter, setPerfSettings, setStatus, setSource, setShifts, keepLinkContext, restoreSavedContext, ownsPart],
  );
}

